// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// Results of the task_queues_* actions, which Harvest serves from the environment's
// task-queue database, and how the Task Queues page shows them.
export var DEAD_LETTER_QUEUE = "electoral_log_dead_letter_queue";
export var OUTCOMES = [
    "succeeded",
    "failed",
    "expired",
    "rejected",
    "discarded",
    "unknown",
];
export var OUTCOME_COLORS = {
    succeeded: "#2e7d32",
    failed: "#d32f2f",
    expired: "#b45309",
    rejected: "#7b1fa2",
    discarded: "#757575",
    unknown: "#616161",
};
export var OUTCOME_HEADER = "x-step-outcome";
export var isOutcome = function (value) { return OUTCOMES.includes(value); };
export var MESSAGE_STATES = ["queued", "archived"];
export var DEAD_LETTER_OPERATIONS = ["replay", "discard"];
export var GRAPH_PERIODS = ["hour", "sixHours", "day", "week"];
export var GRAPH_RANGES = {
    hour: { hours: 1, bucketMinutes: 1 },
    sixHours: { hours: 6, bucketMinutes: 5 },
    day: { hours: 24, bucketMinutes: 15 },
    week: { hours: 168, bucketMinutes: 120 },
};
export var MESSAGE_PAGE_SIZE = 50;
/** The total of a queue's outcomes over the last hour. */
export var processedLastHour = function (queue) {
    return Object.values(queue.last_hour).reduce(function (total, count) { return total + count; }, 0);
};
/** Outcomes in their fixed order, then any the page does not know, by name. */
export var orderedOutcomes = function (outcomes) {
    var known = OUTCOMES.filter(function (outcome) { return outcomes[outcome] !== undefined; }).map(function (outcome) { return [outcome, outcomes[outcome]]; });
    var others = Object.keys(outcomes)
        .filter(function (outcome) { return !isOutcome(outcome); })
        .sort()
        .map(function (outcome) { return [outcome, outcomes[outcome]]; });
    return __spreadArray(__spreadArray([], known, true), others, true);
};
export var outcomeColor = function (outcome) {
    return isOutcome(outcome) ? OUTCOME_COLORS[outcome] : OUTCOME_COLORS.unknown;
};
/**
 * The graph's series, with a point for every bucket of the period, so that a bucket
 * without messages shows as zero rather than being skipped.
 */
export var outcomeSeries = function (result, now) {
    var starts = bucketStarts(result, now);
    var seen = new Set(result.buckets.flatMap(function (bucket) { return Object.keys(bucket.outcomes); }));
    var byStart = new Map(result.buckets.map(function (bucket) { return [Date.parse(bucket.start), bucket]; }));
    return orderedOutcomes(Object.fromEntries(Array.from(seen, function (outcome) { return [outcome, 0]; }))).map(function (_a) {
        var outcome = _a[0];
        return ({
            name: outcome,
            data: starts.map(function (start) { var _a, _b; return [start, (_b = (_a = byStart.get(start)) === null || _a === void 0 ? void 0 : _a.outcomes[outcome]) !== null && _b !== void 0 ? _b : 0]; }),
        });
    });
};
/** Mean wait and processing seconds per bucket; buckets without messages have none. */
export var durationSeries = function (result, now) {
    var starts = bucketStarts(result, now);
    var byStart = new Map(result.buckets.map(function (bucket) { return [Date.parse(bucket.start), bucket]; }));
    var round = function (value) {
        return value === null || value === undefined ? null : Math.round(value * 100) / 100;
    };
    return {
        wait: starts.map(function (start) { var _a; return [start, round((_a = byStart.get(start)) === null || _a === void 0 ? void 0 : _a.mean_wait_secs)]; }),
        processing: starts.map(function (start) { var _a; return [start, round((_a = byStart.get(start)) === null || _a === void 0 ? void 0 : _a.mean_processing_secs)]; }),
    };
};
// Harvest aligns buckets to multiples of their size since the epoch.
var bucketStarts = function (result, now) {
    var size = result.bucket_minutes * 60000;
    var last = Math.floor(now.getTime() / size) * size;
    var first = Math.floor((now.getTime() - result.hours * 3600000) / size) * size;
    var starts = [];
    for (var start = first; start <= last; start += size) {
        starts.push(start);
    }
    return starts;
};
/** Whether a series has any value to plot. */
export var hasValues = function (data) {
    return data.some(function (_a) {
        var value = _a[1];
        return value !== null;
    });
};
var MAX_COUNT_TICKS = 5;
/** Ticks for a count axis: whole numbers only, at most five intervals. */
export var countTicks = function (series) {
    var _a;
    var totals = new Map();
    for (var _i = 0, series_1 = series; _i < series_1.length; _i++) {
        var data = series_1[_i].data;
        for (var _b = 0, data_1 = data; _b < data_1.length; _b++) {
            var _c = data_1[_b], start = _c[0], value = _c[1];
            totals.set(start, ((_a = totals.get(start)) !== null && _a !== void 0 ? _a : 0) + (value !== null && value !== void 0 ? value : 0));
        }
    }
    var highest = Math.max.apply(Math, __spreadArray([0], Array.from(totals.values()), false));
    return Math.max(1, Math.min(MAX_COUNT_TICKS, Math.ceil(highest)));
};
/** An age in the largest units that keep it readable: 45s, 12m 5s, 3h 20m, 2d 4h. */
export var formatAge = function (seconds) {
    if (seconds === null || seconds < 0) {
        return "—";
    }
    var units = [
        ["d", 86400],
        ["h", 3600],
        ["m", 60],
        ["s", 1],
    ];
    var parts = [];
    var rest = Math.floor(seconds);
    for (var _i = 0, units_1 = units; _i < units_1.length; _i++) {
        var _a = units_1[_i], unit = _a[0], size = _a[1];
        var amount = Math.floor(rest / size);
        if (amount > 0 || (parts.length === 0 && size === 1)) {
            parts.push("".concat(amount).concat(unit));
        }
        rest -= amount * size;
        if (parts.length === 2) {
            break;
        }
    }
    return parts.join(" ");
};
export var formatBytes = function (bytes) {
    if (bytes < 1024) {
        return "".concat(bytes, " B");
    }
    if (bytes < 1024 * 1024) {
        return "".concat((bytes / 1024).toFixed(1), " KiB");
    }
    return "".concat((bytes / (1024 * 1024)).toFixed(1), " MiB");
};
/** Operators replay or discard only waiting dead letters. */
export var managesDeadLetters = function (queue, state) {
    return queue === DEAD_LETTER_QUEUE && state === "queued";
};
/** The selected message IDs: a grid's "select all" excludes rather than includes. */
export var selectedMessageIds = function (selection, messages) {
    return messages
        .map(function (message) { return message.msg_id; })
        .filter(function (id) {
        return selection.type === "include" ? selection.ids.has(id) : !selection.ids.has(id);
    });
};
/** The ID to list older messages from, or null when the last page was not full. */
export var nextBefore = function (messages, pageSize) {
    return messages.length < pageSize ? null : String(messages[messages.length - 1].msg_id);
};
/** The electoral-log event a message carries, as "type · election event". */
export var eventLabel = function (event) {
    return [event.message_type, event.election_event_id].filter(Boolean).join(" · ");
};
